import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiMoreVertical } from 'react-icons/fi';
import { Checkbox } from '@mui/material';
import SearchSelect from './SearchSelect';

const getInitials = (name) =>
  name.split(' ').map((n) => n[0]).join('').toUpperCase();

// ---- PortalMenu (only the root <div> changed)
function PortalMenu({ anchorRef, onClose, children, align = 'right' }) {
  const menuRef = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const place = () => {
      const a = anchorRef.current;
      if (!a) return;
      const r = a.getBoundingClientRect();
      const top = r.bottom + window.scrollY + 4;
      const left = (align === 'right' ? r.right : r.left) + window.scrollX;
      setStyle({ top, left });
    };
    place();

    const onScrollOrResize = () => onClose?.();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorRef, align, onClose]);

  return createPortal(
    <div
      ref={menuRef}
      data-portal-menu
      // prevent the table’s document mousedown listener from closing the menu
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: style.top,
        left: style.left,
        transform: align === 'right' ? 'translateX(-100%)' : 'none',
      }}
      className="w-36 bg-white border rounded shadow-md z-[9999]"
    >
      {children}
    </div>,
    document.body
  );
}

export default function TeamMemberTable({ title, members, options, addTeamMember, count, rep_level_count, onRemove }) {
  const [search, setSearch] = useState('');
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [headerDropdownOpen, setHeaderDropdownOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const triggerRefs = useRef({});              // per-row trigger buttons
  const headerTriggerRef = useRef(null);       // header trigger

  const toggleDropdown = (id) => {
    setDropdownOpenId((prev) => (prev === id ? null : id));
  };
  const toggleHeaderDropdown = () => {
    setHeaderDropdownOpen((p) => !p);
  };

  useEffect(() => {
    const onDocClick = (e) => {
      // close if clicking outside any open menu trigger
      if (dropdownOpenId !== null) {
        const t = triggerRefs.current[dropdownOpenId];
        if (t && !t.contains(e.target)) setDropdownOpenId(null);
      }
      if (headerDropdownOpen) {
        const ht = headerTriggerRef.current;
        if (ht && !ht.contains(e.target)) setHeaderDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [dropdownOpenId, headerDropdownOpen]);

  const handleCheck = (id) => {
    setSelected((prev) => {
      const updated = new Set(prev);
      updated.has(id) ? updated.delete(id) : updated.add(id);
      return updated;
    });
  };

  const handleBulkRemove = () => {
    selected.forEach((id) => onRemove(id));
    setSelected(new Set());
    setHeaderDropdownOpen(false);
  };

  const filtered = members.filter((m) =>
    m.name.toLowerCase().includes(search.toLowerCase()) || m.badge_id.includes(search)
  );

  return (
    <div className="w-full border rounded-md p-2 sm:p-4 bg-white shadow-sm">
      <SearchSelect addTeamMember={addTeamMember} options={options} count={count} title={title} />

      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-700">{title}s</h3>
        <div className="flex flex-col justify-center items-end gap-0">
          {rep_level_count.map((obj) =>
            obj.count > 0 && (
              <div key={obj.count + obj.role} className="flex justify-end items-end text-xs text-right text-gray-500 mt-2">
                {count && (
                  <div className="flex items-end text-xs">
                    <p>{obj.current_count >= obj.count && <span className="ml-1 text-green-600">✅</span>}</p>
                    <p className="max-w-max text-xs">{obj.current_count} of {obj.count} {obj.role}s</p>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>

      {/* This container can keep scrolling; menus will now escape it */}
      <div className="overflow-y-scroll max-h-64 sm:max-h-80">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th></th>
              <th>Name</th>
              <th>Role</th>
              <th className="px-2 py-2 text-right">
                <button ref={headerTriggerRef} onClick={toggleHeaderDropdown}>
                  <FiMoreVertical className="text-gray-500" />
                </button>
                {headerDropdownOpen && (
                  <PortalMenu anchorRef={headerTriggerRef} onClose={() => setHeaderDropdownOpen(false)}>
                    <button
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-red-100 text-red-600 disabled:opacity-50"
                      onClick={handleBulkRemove}
                      disabled={selected.size === 0}
                    >
                      Remove Selected
                    </button>
                  </PortalMenu>
                )}
              </th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className="border-t group">
                <td className="py-2">
                  <Checkbox checked={selected.has(m.id)} onChange={() => handleCheck(m.id)} />
                </td>
                <td className="flex items-center gap-2 py-2 text-xs sm:text-sm">
                  <div className="hidden sm:flex w-12 h-12 rounded-full items-center justify-center bg-gray-200 text-gray-700 font-semibold py-2">
                    {getInitials(m.name)}
                  </div>
                  <p className="py-2">{m.name}</p>
                </td>
                <td className="py-2">
                  <div className={`${m.role === 'Rookie' ? 'text-gray-500 bg-gray-100' : 'text-yellow-500 bg-yellow-100'} p-2 rounded-full text-center text-xs`}>
                    {m.role}
                  </div>
                </td>
                <td className="px-2 py-2 text-right">
                  <button
                    ref={(el) => (triggerRefs.current[m.id] = el)}
                    onClick={() => toggleDropdown(m.id)}
                  >
                    <FiMoreVertical className="text-gray-500" />
                  </button>

                  {dropdownOpenId === m.id && (
                    <PortalMenu
                      anchorRef={{ current: triggerRefs.current[m.id] }}
                      onClose={() => setDropdownOpenId(null)}
                    >
                      <button
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-red-100 text-red-600"
                        onClick={() => {
                          setDropdownOpenId(null);
                          onRemove(m.id);
                        }}
                      >
                        Remove
                      </button>
                    </PortalMenu>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
